import React, { useState } from "react";

const ManageGroupModal = ({
  isOpen,
  onClose,
  manageGroup,
  manageTab,
  setManageTab,
  manageMembers,
  isLoadingMembers,
  isRemovingMemberId,
  onOpenRemoveMemberModal,
  avatarFallbackMap,
  setAvatarFallbackMap,
  joinRequests,
  isLoadingRequests,
  onAcceptRequest,
  onDeclineRequest,
  directInviteQuery,
  setDirectInviteQuery,
  directInviteResults,
  isSearchingDirect,
  directInviteError,
  setDirectInviteError,
  invitingUserId,
  onSearchDirectUser,
  onDirectInvite,
  manageInviteCode,
  isGeneratingManageCode,
  onGenerateManageCode,
  onCopyInviteCode,
  onBatchRemoveMembers,
  isBatchRemovingMembers,
}) => {
  const [selectedMemberIds, setSelectedMemberIds] = useState(() => new Set());

  if (!isOpen) return null;

  const removableMembers = manageMembers.filter((m) => !m.isOwner);
  const removableIds = removableMembers.map((m) => String(m.userId));
  const isAllRemovableSelected =
    removableIds.length > 0 && removableIds.every((id) => selectedMemberIds.has(id));

  const toggleSelectAllMembers = () => {
    if (isAllRemovableSelected) {
      setSelectedMemberIds(new Set());
    } else {
      setSelectedMemberIds(new Set(removableIds));
    }
  };

  const toggleSelectMember = (userId) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      const strId = String(userId);
      if (next.has(strId)) next.delete(strId);
      else next.add(strId);
      return next;
    });
  };

  const handleBatchRemoveClick = () => {
    if (selectedMemberIds.size === 0 || !onBatchRemoveMembers) return;
    onBatchRemoveMembers(Array.from(selectedMemberIds), () => setSelectedMemberIds(new Set()));
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-[120] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col border border-gray-100">
        {/* Modal Header */}
        <div className="px-6 md:px-8 py-5 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                Admin Portal
              </span>
              <span className="text-xs text-gray-400 font-medium">{manageGroup?.subject}</span>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
              {manageGroup?.name || "Manage Study Group"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition flex items-center justify-center"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        {/* Modal Tab Navigation */}
        <div className="px-6 md:px-8 bg-gray-50 border-b border-gray-100 flex items-center gap-2 shrink-0 overflow-x-auto">
          <button
            type="button"
            onClick={() => setManageTab("members")}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
              manageTab === "members"
                ? "border-blue-600 text-blue-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            <i className="fa-solid fa-users text-xs"></i>
            <span>Members ({manageMembers.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setManageTab("requests")}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
              manageTab === "requests"
                ? "border-blue-600 text-blue-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            <i className="fa-solid fa-user-clock text-xs"></i>
            <span>Pending Requests</span>
            {joinRequests.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-red-500 text-white font-bold animate-pulse">
                {joinRequests.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setManageTab("direct")}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
              manageTab === "direct"
                ? "border-blue-600 text-blue-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            <i className="fa-solid fa-envelope text-xs"></i>
            <span>Direct Invite</span>
          </button>
          <button
            type="button"
            onClick={() => setManageTab("invite")}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
              manageTab === "invite"
                ? "border-blue-600 text-blue-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            <i className="fa-solid fa-key text-xs"></i>
            <span>Invite Code</span>
          </button>
        </div>

        {/* Modal Body Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50/50">
          {/* TAB 1: MEMBERS */}
          {manageTab === "members" && (
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-base font-bold text-gray-900">Current Members</h4>
                  <p className="text-xs text-gray-500">
                    {manageMembers.length} of {manageGroup?.maxMembers || 5} seats occupied
                  </p>
                </div>
                {removableMembers.length > 0 && (
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer select-none px-2.5 py-1.5 rounded-xl hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={isAllRemovableSelected}
                      onChange={toggleSelectAllMembers}
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                    />
                    <span>Select All ({removableMembers.length})</span>
                  </label>
                )}
              </div>

              {/* Bulk Action Banner */}
              {selectedMemberIds.size > 0 && (
                <div className="flex items-center justify-between p-3.5 bg-red-50 border border-red-200 rounded-2xl animate-in fade-in duration-150">
                  <span className="text-xs font-semibold text-red-800 flex items-center gap-1.5">
                    <i className="fa-solid fa-check-double text-red-600"></i>
                    <span>{selectedMemberIds.size} member{selectedMemberIds.size > 1 ? "s" : ""} selected</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedMemberIds(new Set())}
                      className="px-2.5 py-1 text-xs font-semibold text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      disabled={isBatchRemovingMembers}
                      onClick={handleBatchRemoveClick}
                      className="px-3.5 py-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-xs flex items-center gap-1.5 transition active:scale-95 disabled:opacity-50"
                    >
                      {isBatchRemovingMembers ? (
                        <>
                          <i className="fa-solid fa-circle-notch fa-spin text-xs"></i>
                          <span>Removing...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-user-xmark text-xs"></i>
                          <span>Remove Selected ({selectedMemberIds.size})</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {isLoadingMembers ? (
                <div className="p-8 text-center text-sm text-gray-400">
                  <i className="fa-solid fa-circle-notch fa-spin mr-2"></i>
                  <span>Loading members...</span>
                </div>
              ) : manageMembers.length === 0 ? (
                <div className="p-8 text-center bg-white rounded-2xl border border-gray-100 text-sm text-gray-500">
                  No members found in this group.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {manageMembers.map((member) => {
                    const fullName =
                      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
                      "Group Member";
                    const initial = (member.firstName?.charAt(0) || "U").toUpperCase();
                    const isSelected = selectedMemberIds.has(String(member.userId));

                    return (
                      <div
                        key={member.userId}
                        className={`bg-white p-4 rounded-2xl border transition flex items-center justify-between gap-3 ${
                          isSelected
                            ? "border-blue-300 bg-blue-50/40 shadow-xs ring-1 ring-blue-300"
                            : "border-gray-100 shadow-xs"
                        }`}
                      >
                        <div className="flex items-center gap-3.5 min-w-0">
                          {!member.isOwner && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectMember(member.userId)}
                              className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer shrink-0 transition"
                              aria-label={`Select ${fullName}`}
                            />
                          )}
                          {member.avatar && !avatarFallbackMap[String(member.userId)] ? (
                            <img
                              src={member.avatar}
                              alt={fullName}
                              onError={() =>
                                setAvatarFallbackMap((prev) => ({
                                  ...prev,
                                  [String(member.userId)]: true,
                                }))
                              }
                              className="w-11 h-11 rounded-full object-cover border border-gray-200 shrink-0"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-base shrink-0">
                              {initial}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 text-sm truncate">{fullName}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {member.email || "No email available"}
                            </p>
                          </div>
                        </div>

                        <div>
                          {member.isOwner ? (
                            <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200/80 flex items-center gap-1.5">
                              <i className="fa-solid fa-crown text-[10px]"></i> Group Owner
                            </span>
                          ) : (
                            <button
                              type="button"
                              disabled={isRemovingMemberId === String(member.userId)}
                              onClick={() => onOpenRemoveMemberModal(member)}
                              className="px-3 py-1.5 rounded-xl text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 transition flex items-center gap-1.5"
                            >
                              {isRemovingMemberId === String(member.userId) ? (
                                <>
                                  <i className="fa-solid fa-circle-notch fa-spin"></i>
                                  <span>Removing...</span>
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-user-minus text-[10px]"></i>
                                  <span>Remove</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: JOIN REQUESTS */}
          {manageTab === "requests" && (
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-base font-bold text-gray-900">Pending Join Requests</h4>
                  <p className="text-xs text-gray-500">
                    Students requesting access via this group&apos;s invite code
                  </p>
                </div>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                  {joinRequests.length} Pending
                </span>
              </div>

              {isLoadingRequests ? (
                <div className="p-8 text-center text-sm text-gray-400">
                  <i className="fa-solid fa-circle-notch fa-spin mr-2"></i>
                  <span>Loading requests...</span>
                </div>
              ) : joinRequests.length === 0 ? (
                <div className="p-10 text-center bg-white rounded-2xl border border-gray-100">
                  <div className="w-12 h-12 rounded-full bg-gray-50 text-gray-400 flex items-center justify-center mx-auto mb-3 text-lg">
                    <i className="fa-solid fa-inbox"></i>
                  </div>
                  <p className="text-sm font-bold text-gray-800">No pending join requests</p>
                  <p className="text-xs text-gray-400 mt-1">
                    When classmates enter your invite code, their requests will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {joinRequests.map((req) => {
                    const user = req.user;
                    const fullName =
                      `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Student";
                    const initial = (user.firstName?.charAt(0) || "U").toUpperCase();

                    return (
                      <div
                        key={req._id}
                        className="flex items-center justify-between gap-3 p-4 rounded-2xl border border-orange-100 bg-white shadow-xs"
                      >
                        <div className="flex items-center gap-3.5 min-w-0">
                          {user.avatar && !avatarFallbackMap[String(user._id)] ? (
                            <img
                              src={user.avatar}
                              alt={fullName}
                              onError={() =>
                                setAvatarFallbackMap((prev) => ({
                                  ...prev,
                                  [String(user._id)]: true,
                                }))
                              }
                              className="w-11 h-11 rounded-full object-cover border border-orange-200 shrink-0"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-base shrink-0">
                              {initial}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">{fullName}</p>
                            <p className="text-xs text-gray-400 truncate">
                              Requested on {new Date(req.requestedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => onDeclineRequest(req._id)}
                            className="px-3 py-2 rounded-xl text-xs font-bold text-gray-600 bg-gray-100 hover:bg-red-50 hover:text-red-600 transition flex items-center gap-1.5"
                            title="Decline request"
                          >
                            <i className="fa-solid fa-xmark"></i>
                            <span>Decline</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => onAcceptRequest(req._id)}
                            className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm flex items-center gap-1.5"
                            title="Accept request"
                          >
                            <i className="fa-solid fa-check"></i>
                            <span>Accept</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: DIRECT INVITE */}
          {manageTab === "direct" && (
            <div className="max-w-2xl mx-auto space-y-5">
              <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-base font-bold text-gray-900">Direct Invite</h4>
                    <p className="text-xs text-gray-500">
                      Search by student name or email to directly add them to this group.
                    </p>
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                    {manageMembers.length} / {manageGroup?.maxMembers || 5} Seats
                  </span>
                </div>

                <form onSubmit={onSearchDirectUser} className="flex gap-2">
                  <div className="relative flex-1">
                    <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                    <input
                      type="text"
                      value={directInviteQuery}
                      onChange={(e) => {
                        setDirectInviteQuery(e.target.value);
                        if (directInviteError) setDirectInviteError("");
                      }}
                      placeholder="Search student by name or email..."
                      className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSearchingDirect || !directInviteQuery.trim()}
                    className="px-4 py-2.5 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-xs flex items-center gap-1.5 shrink-0"
                  >
                    {isSearchingDirect ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin"></i>
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-search"></i>
                        <span>Search</span>
                      </>
                    )}
                  </button>
                </form>

                {directInviteError && (
                  <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-xs flex items-center gap-2">
                    <i className="fa-solid fa-circle-exclamation shrink-0"></i>
                    <span>{directInviteError}</span>
                  </div>
                )}
              </div>

              {/* Search Results List */}
              {directInviteResults.length > 0 && (
                <div className="space-y-2.5">
                  <h5 className="text-xs font-bold uppercase tracking-wider text-gray-400 px-1">
                    Matching Students ({directInviteResults.length})
                  </h5>
                  {directInviteResults.map((u) => {
                    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Student";
                    const initial = (u.firstName?.charAt(0) || "U").toUpperCase();
                    const isAlreadyMember = manageMembers.some(
                      (m) => String(m.userId) === String(u._id)
                    );
                    const isCapacityFull = manageMembers.length >= Number(manageGroup?.maxMembers || 5);
                    const isInviting = invitingUserId === String(u._id);

                    return (
                      <div
                        key={u._id}
                        className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3.5 min-w-0">
                          {u.avatar ? (
                            <img
                              src={u.avatar}
                              alt={fullName}
                              className="w-10 h-10 rounded-full object-cover border border-gray-200 shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm shrink-0">
                              {initial}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">{fullName}</p>
                            <p className="text-xs text-gray-400 truncate">{u.email}</p>
                          </div>
                        </div>

                        <div className="shrink-0">
                          {isAlreadyMember ? (
                            <span className="text-xs font-bold px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-1.5">
                              <i className="fa-solid fa-check text-[10px]"></i> In Group
                            </span>
                          ) : isCapacityFull ? (
                            <span className="text-xs font-bold px-3 py-1.5 rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed">
                              Group Full
                            </span>
                          ) : (
                            <button
                              type="button"
                              disabled={isInviting}
                              onClick={() => onDirectInvite(u)}
                              className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition shadow-xs flex items-center gap-1.5"
                            >
                              {isInviting ? (
                                <>
                                  <i className="fa-solid fa-circle-notch fa-spin"></i>
                                  <span>Adding...</span>
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-user-plus text-[10px]"></i>
                                  <span>Add to Group</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: INVITE CODE & ACCESS */}
          {manageTab === "invite" && (
            <div className="max-w-xl mx-auto space-y-6">
              <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
                <h4 className="text-base font-bold text-gray-900 mb-1">Active Invite Code</h4>
                <p className="text-xs text-gray-500 mb-5">
                  Share this code with classmates. Only one active code exists at any time.
                </p>

                <div className="p-4 rounded-2xl bg-blue-50 border border-blue-100 text-center mb-4">
                  <p className="text-[11px] font-bold text-blue-600 uppercase tracking-widest mb-1">
                    Group Code
                  </p>
                  <p className="text-2xl font-mono font-extrabold text-blue-900 tracking-wider">
                    {manageInviteCode || "No active code"}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={!manageInviteCode}
                    onClick={onCopyInviteCode}
                    className={`flex-1 py-2.5 rounded-xl font-bold text-xs transition flex items-center justify-center gap-2 ${
                      manageInviteCode
                        ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <i className="fa-solid fa-copy text-xs"></i>
                    <span>Copy Code</span>
                  </button>

                  <button
                    type="button"
                    disabled={isGeneratingManageCode}
                    onClick={onGenerateManageCode}
                    className={`flex-1 py-2.5 rounded-xl font-bold text-xs transition flex items-center justify-center gap-2 ${
                      isGeneratingManageCode
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {isGeneratingManageCode ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin text-xs"></i>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-arrows-rotate text-xs"></i>
                        <span>Regenerate Code</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-amber-50/70 border border-amber-200/70 rounded-2xl p-4 text-xs text-amber-800 flex items-start gap-3">
                <i className="fa-solid fa-triangle-exclamation text-amber-500 mt-0.5 text-sm shrink-0"></i>
                <div>
                  <p className="font-bold">Important note on code regeneration</p>
                  <p className="text-amber-700 mt-0.5 leading-relaxed">
                    Generating a new invite code immediately invalidates any previously shared
                    code. Existing group members will remain in the group.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageGroupModal;
